import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { getHumanTimeFormatFromMilliseconds } from '../utils/time';

const INTERVAL = 1000;

export const useInterval = (callback: () => void, delay: number) => {
  const savedCallback = useRef(callback);

  useLayoutEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    let id: NodeJS.Timeout;

    if (delay) {
      id = setInterval(() => savedCallback.current(), delay);
    }

    return () => clearInterval(id);
  }, [delay]);
};

export const useTimer = ({ isIncrement, timeToCount }: { isIncrement: boolean; timeToCount?: number }) => {
  const [time, setTime] = useState<number>(!timeToCount ? 0 : timeToCount);
  const [running, setRunning] = useState<boolean>(false);

  useEffect(() => {
    if (timeToCount) {
      setTime(timeToCount);
    }
  }, [timeToCount]);

  const start = useCallback(() => {
    setRunning(true);
  }, []);

  const pause = useCallback(() => {
    setRunning(false);
  }, []);

  const reset = useCallback(() => {
    pause();
    setTime(timeToCount || 0);
  }, [pause, timeToCount]);

  const increment = useCallback(() => {
    setTime(time + INTERVAL);
  }, [time]);

  const decrement = useCallback(() => {
    if (time <= 0) {
      reset();
      return;
    }
    setTime(time - INTERVAL);
  }, [time, reset]);

  useInterval(isIncrement ? increment : decrement, running ? INTERVAL : 0);

  return { start, pause, reset, time: getHumanTimeFormatFromMilliseconds(time), rawTime: time };
};
